import React, { useState, useEffect, useCallback, useRef } from "react";
import { useStudent } from "../context/StudentContext";
import { API_URL } from "../config";
import { formatRelativeDate } from "../utils/formatters";
import { getAnnouncementsBundle } from "../utils/announcementsCache";
import AnnouncementInteractiveResources from "./AnnouncementInteractiveResources";
import "./Announcements.css";

const Announcements = ({ variant = "sidebar" }) => {
  const isPage = variant === "page";
  const { activeCourse } = useStudent();
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [pendingReadIds, setPendingReadIds] = useState(() => new Set());
  const pendingReadIdsRef = useRef(new Set());

  const rollbackRead = useCallback((id) => {
    setAnnouncements((prev) =>
      prev.map((a) => (a._id === id ? { ...a, read: false } : a))
    );
    setUnreadCount((c) => c + 1);
  }, []);

  const fetchSeqRef = useRef(0);
  const fetchAbortRef = useRef(null);

  const fetchAnnouncements = useCallback(async (signal) => {
    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setLoading(true);
    setError(null);
    try {
      const { announcements: nextAnnouncements, unreadCount: nextUnreadCount } =
        await getAnnouncementsBundle();

      if (signal?.aborted || seq !== fetchSeqRef.current) return;

      setAnnouncements(nextAnnouncements);
      setUnreadCount(nextUnreadCount);

    } catch (err) {
      if (err.name === "AbortError" || signal?.aborted) return;
      if (seq !== fetchSeqRef.current) return;
      console.error("Error fetching announcements:", err);
      setError("Error al cargar comunicados");
    } finally {
      if (!signal?.aborted && seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    fetchAnnouncements(controller.signal);
    return () => {
      controller.abort();
      if (fetchAbortRef.current === controller) {
        fetchAbortRef.current = null;
      }
    };
  }, [fetchAnnouncements, activeCourse?._id]);

  const handleRetryFetch = () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    fetchAnnouncements(controller.signal);
  };

  const markAsRead = async (id) => {
    if (pendingReadIdsRef.current.has(id)) return;
    pendingReadIdsRef.current.add(id);
    setPendingReadIds(new Set(pendingReadIdsRef.current));
    try {
      const res = await fetch(`${API_URL}/announcements/${id}/read`, {
        method: "PATCH",
        credentials: "include",
      });
      if (!res.ok) {
        rollbackRead(id);
      }
    } catch {
      rollbackRead(id);
    } finally {
      pendingReadIdsRef.current.delete(id);
      setPendingReadIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleAnnouncementClick = (ann) => {
    setExpandedId(expandedId === ann._id ? null : ann._id);
      if (
        !ann.read &&
        !pendingReadIdsRef.current.has(ann._id) &&
        !pendingReadIds.has(ann._id)
        ) {
          setAnnouncements((prev) =>
              prev.map((a) => (a._id === ann._id ? { ...a, read: true } : a))
            );
            setUnreadCount((c) => Math.max(0, c - 1));
          markAsRead(ann._id);
        }
  };

  const filtered = activeCourse
    ? announcements.filter(
        (a) => {
          if (a.scope === "global") return true;
          if (!a.courseId) return false;
          const announcementCourseId = typeof a.courseId === "object" && a.courseId._id 
            ? String(a.courseId._id) 
            : String(a.courseId);
          return announcementCourseId === String(activeCourse._id);
        }
      )
    : announcements.filter((a) => a.scope === "global");

  const panelStyle = isPage
    ? {
        padding: "24px",
        backgroundColor: "#0f223a",
        height: "100%",
        color: "white",
        minWidth: "100%",
        overflowY: "auto",
      }
    : {
        padding: "20px",
        backgroundColor: "#0f223a",
        borderLeft: "1px solid #17a398",
        height: "100%",
        color: "white",
        minWidth: "250px",
        overflowY: "auto",
      };

  return (
    <div
      className={`announcements-panel${isPage ? " announcements-panel--page" : ""}`}
      style={panelStyle}
    >
      <h3
        style={{
          borderBottom: "1px solid #17a398",
          paddingBottom: "10px",
          color: "#17a398",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        Comunicados
        {unreadCount > 0 && (
          <span
            style={{
              backgroundColor: "#e74c3c",
              color: "white",
              borderRadius: "50%",
              minWidth: "22px",
              height: "22px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </h3>
      <div className="announcement-list">
        {loading ? (
          <p className="announcements-muted">Cargando...</p>
        ) : error ? (
          <div style={{ marginTop: "20px" }}>
            <p style={{ color: "#e74c3c" }}>{error}</p>
            <button
              type="button"
              className="side-sidebar-button"
              style={{ marginTop: "8px" }}
              onClick={handleRetryFetch}
            >
              Reintentar
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="announcements-muted">
            {activeCourse
              ? "No hay comunicados para este curso."
              : "No hay comunicados disponibles."}
          </p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0 0" }}>
            {filtered.map((ann) => (
              <li
                key={ann._id}
                style={{
                  marginBottom: "12px",
                  padding: "12px",
                  backgroundColor: ann.read ? "rgba(23,163,152,0.08)" : "rgba(23,163,152,0.15)",
                  borderRadius: "6px",
                  borderLeft: ann.read ? "2px solid transparent" : "2px solid #17a398",
                  cursor: "pointer",
                }}
              >
                <button
                  type="button"
                  onClick={() => handleAnnouncementClick(ann)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    textAlign: "left",
                    width: "100%",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    {!ann.read && (
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "#17a398",
                          flexShrink: 0,
                          marginTop: "6px",
                        }}
                        aria-hidden
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ display: "block", marginBottom: "4px", color: "#e8f4f2" }}>
                        {ann.title}
                      </strong>
                      {ann.scope === "course" && ann.courseId?.titulo && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#17a398",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          {ann.courseId.titulo}
                        </span>
                      )}
                      {expandedId === ann._id ? (
                        <p
                          style={{
                            margin: "8px 0 0 0",
                            fontSize: "14px",
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            color: "#e8f4f2",
                          }}
                        >
                          {ann.content}
                        </p>
                      ) : (
                        <p
                          style={{
                            margin: "4px 0 0 0",
                            fontSize: "13px",
                            color: "#cbd5e1",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ann.content}
                        </p>
                      )}
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#94a3b8",
                          marginTop: "6px",
                          display: "block",
                        }}
                      >
                        {formatRelativeDate(ann.createdAt)}
                        {ann.displayName && ` · ${ann.displayName}`}
                      </span>
                    </div>
                  </div>
                </button>
                {expandedId === ann._id ? (
                  <AnnouncementInteractiveResources
                    announcementId={ann._id}
                    links={ann.links}
                    attachments={ann.attachments}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Announcements;
